/**
 * Порядок прохождения и прогресс игры (docs/GAME.md).
 *
 * Прогресс НИГДЕ не хранится: реплика пройдена, если у неё есть активный дубль.
 * Выводимое состояние невозможно рассинхронизировать с реальностью — записал
 * в редакторе, игра об этом уже знает.
 */

import type { Line, Role, Take } from '../api/types'
import type { PlayOrder } from './settings'

export const NO_ROLE_NAME = 'Без роли'
export const NO_ROLE_COLOR = '#5a6577'

export type PlayItem = {
  line: Line
  role: Role | null
  roleName: string
  roleColor: string
  /** Показать экран передачи устройства перед этой репликой. */
  handoff: boolean
  /** Сколько реплик подряд озвучивает эта роль, начиная с текущей. */
  segmentSize: number
}

function byTime(a: Line, b: Line): number {
  if (a.start !== b.start) return a.start - b.start
  if (a.end !== b.end) return a.end - b.end
  return a.id.localeCompare(b.id)
}

/** Группировка по ролям: порядок ролей — по первому появлению в видео. */
function groupByRole(sorted: Line[]): Line[] {
  const order: string[] = []
  const groups = new Map<string, Line[]>()
  for (const line of sorted) {
    const existing = groups.get(line.roleId)
    if (existing) {
      existing.push(line)
    } else {
      groups.set(line.roleId, [line])
      order.push(line.roleId)
    }
  }
  const result: Line[] = []
  for (const roleId of order) {
    for (const line of groups.get(roleId) ?? []) result.push(line)
  }
  return result
}

/**
 * Плейлист игры. Реплики с пустой длительностью выбрасываем: записывать
 * нечего, а автостоп по `end + tail` сработал бы мгновенно.
 *
 * `handoffBetweenRoles` — тумблер «играем вдвоём» со стартового экрана;
 * на `by-role` он не влияет, там заставка есть всегда.
 */
export function buildPlaylist(
  lines: Line[],
  roles: Role[],
  order: PlayOrder,
  handoffBetweenRoles = false,
): PlayItem[] {
  const usable = lines.filter((line) => line.end > line.start)
  const sorted = [...usable].sort(byTime)
  const ordered = order === 'by-role' ? groupByRole(sorted) : sorted

  const roleById = new Map(roles.map((role) => [role.id, role]))
  const distinctRoles = new Set(ordered.map((line) => line.roleId)).size

  const items: PlayItem[] = ordered.map((line) => {
    const role = roleById.get(line.roleId) ?? null
    const name = role?.name.trim()
    return {
      line,
      role,
      roleName: name ? name : NO_ROLE_NAME,
      roleColor: role?.color ? role.color : NO_ROLE_COLOR,
      handoff: false,
      segmentSize: 1,
    }
  })

  // Сколько реплик роль отыграет подряд — это число уходит в заставку передачи.
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const item = items[i] as PlayItem
    const next = i + 1 < items.length ? (items[i + 1] as PlayItem) : null
    item.segmentSize = next && next.line.roleId === item.line.roleId ? next.segmentSize + 1 : 1
  }

  // Заставка — на смене роли и только если ролей больше одной. Перед самой
  // первой репликой её нет: устройство и так у того, кто нажал «Начать».
  // В sequential заставка появляется, только если игрок сам её попросил:
  // иначе диалог на двоих превратился бы в бесконечное «Готов».
  const handoffEnabled = order === 'by-role' || handoffBetweenRoles
  for (let i = 1; i < items.length; i += 1) {
    const item = items[i] as PlayItem
    const prev = items[i - 1] as PlayItem
    item.handoff = handoffEnabled && distinctRoles > 1 && item.line.roleId !== prev.line.roleId
  }

  return items
}

/** Реплики с активным дублем — то есть пройденные. */
export function activeLineIds(takes: Take[]): Set<string> {
  const ids = new Set<string>()
  for (const take of takes) if (take.isActive) ids.add(take.lineId)
  return ids
}

export function countRecorded(items: PlayItem[], recorded: ReadonlySet<string>): number {
  let done = 0
  for (const item of items) if (recorded.has(item.line.id)) done += 1
  return done
}

/** Индекс первой незаписанной реплики — точка восстановления после перезагрузки. */
export function firstUnrecorded(items: PlayItem[], recorded: ReadonlySet<string>): number {
  const index = items.findIndex((item) => !recorded.has(item.line.id))
  return index === -1 ? 0 : index
}

export type RoleProgress = {
  roleId: string
  name: string
  color: string
  total: number
  done: number
}

/** Кто сколько озвучил — для финального экрана. */
export function roleProgress(items: PlayItem[], recorded: ReadonlySet<string>): RoleProgress[] {
  const byRole = new Map<string, RoleProgress>()
  for (const item of items) {
    const key = item.line.roleId
    let entry = byRole.get(key)
    if (!entry) {
      entry = { roleId: key, name: item.roleName, color: item.roleColor, total: 0, done: 0 }
      byRole.set(key, entry)
    }
    entry.total += 1
    if (recorded.has(item.line.id)) entry.done += 1
  }
  return [...byRole.values()]
}
